'use client';

import { FC, useCallback, useEffect, useRef, useState } from 'react';
import FadeIn from '@/components/FadeIn/FadeIn';
import { getImageUrl } from '@/lib/cdn';

type IGrowSectionProps = object;

interface IHeroTestimonial {
  quote: string;
  name: string;
  role: string;
  company: string;
  linkedinUrl: string;
  videoUrl?: string;
  metric: { value: string; label: string };
  img: string;
}

interface IMiniTestimonial {
  quote: string;
  name: string;
  role: string;
  company: string;
  linkedinUrl: string;
  videoUrl?: string;
  img: string;
}

const PROFILE_CDN = 'https://assets.marketbetter.ai/profiles';

const HERO: IHeroTestimonial = {
  quote: 'marketbetter is a lifeline to our sales team. We stopped list-building and started booking meetings — same week.',
  name: 'Elizabeth Parks',
  role: 'CEO',
  company: 'Parks Associates',
  linkedinUrl: 'https://www.linkedin.com/in/elizabethparks-parksassociates',
  videoUrl: 'https://assets.marketbetter.ai/videos/testimonial_2_new.mp4',
  metric: { value: '+63%', label: 'meetings booked, Q1 2026' },
  img: `${PROFILE_CDN}/elizabeth-parks.jpg`,
};

const MINIS: IMiniTestimonial[] = [
  {
    quote: 'Every email and LinkedIn message is personalized. My team can focus on selling.',
    name: 'Charlie Evans',
    role: 'Head of Sales',
    company: 'Healthcare IT Leaders',
    linkedinUrl: 'https://www.linkedin.com/in/charles-evans-iii-455607215',
    img: `${PROFILE_CDN}/charlie-evans.jpeg`,
  },
  {
    quote: 'We cut follow-up time from days to minutes — growing 2× faster.',
    name: 'Francis Gallic',
    role: 'CEO',
    company: 'HelixWireless',
    linkedinUrl: 'https://www.linkedin.com/in/fgallic',
    videoUrl: 'https://assets.marketbetter.ai/videos/testimonial_4_new.mp4',
    img: `${PROFILE_CDN}/francis-gallic.jpeg`,
  },
  {
    quote: 'I can focus on selling instead of prospecting.',
    name: 'Jarret Steiner',
    role: 'Regional Sales Manager',
    company: 'Ramen Inc',
    linkedinUrl: 'https://www.linkedin.com/in/jarret-steiner-00946311/',
    videoUrl: 'https://assets.marketbetter.ai/videos/testimonial_6_new.mp4',
    img: `${PROFILE_CDN}/jarret-steiner.png`,
  },
  {
    quote: 'marketbetter has given me my time back.',
    name: 'Crystal Misko',
    role: 'Account Executive',
    company: 'Hologram.io',
    linkedinUrl: 'https://www.linkedin.com/in/crystalmisko/',
    videoUrl: 'https://assets.marketbetter.ai/videos/testimonial_5_new.mp4',
    img: `${PROFILE_CDN}/crystal-misko.png`,
  },
  {
    quote: 'It tells me who\'s on our site, what they click, and what to send. Cold outreach runs itself.',
    name: 'Juan Dewendt',
    role: 'SDR',
    company: 'Kajeet Inc',
    linkedinUrl: 'https://www.linkedin.com/in/juan-dewendt-17327721a',
    img: `${PROFILE_CDN}/juan-dewendt.jpeg`,
  },
];

const BLUE_500 = '#094FFE';
const BLUE_800 = '#04216B';
const MINT_300 = '#79EEC1';
const MINT_400 = '#50D5A2';
const SLATE_100 = '#F1F5F9';
const SLATE_200 = '#E2E8F0';
const SLATE_500 = '#64748B';
const SLATE_600 = '#475569';
const SLATE_800 = '#1E293B';
const SLATE_900 = '#0F172A';
const BORDER = SLATE_200;

const FONT_DISPLAY = 'var(--font-plus-jakarta-sans), "Plus Jakarta Sans", Helvetica, Arial, sans-serif';
const FONT_BODY = 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif';

const StarRow: FC = () => (
  <div className='flex items-center justify-end gap-1' style={{ color: '#FF9F14' }}>
    {[...Array(5)].map((_, i) => (
      <svg key={i} width='16' height='16' viewBox='0 0 20 20' fill='currentColor' aria-hidden>
        <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
      </svg>
    ))}
  </div>
);

const QuoteGlyph: FC = () => (
  <svg width='36' height='28' viewBox='0 0 40 32' fill={BLUE_500} style={{ opacity: 0.9 }} aria-hidden>
    <path d='M0 32V18C0 8 5.6 2 16 0v7c-4.8 1.2-7.2 4-7.2 8H16v17H0zm24 0V18c0-10 5.6-16 16-18v7c-4.8 1.2-7.2 4-7.2 8H40v17H24z' />
  </svg>
);

interface VideoModalPayload {
  videoUrl: string;
  name: string;
  role?: string;
  company?: string;
}

const WatchBadge: FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type='button'
    onClick={onClick}
    className='inline-flex items-center gap-[7px] whitespace-nowrap'
    style={{
      padding: '9px 14px',
      borderRadius: 999,
      border: `1px solid ${BORDER}`,
      background: '#fff',
      color: SLATE_800,
      fontFamily: FONT_BODY,
      fontSize: 13,
      fontWeight: 500,
    }}
  >
    {/* eslint-disable-next-line @next/next/no-img-element */}
    <img src={getImageUrl('icons/play.svg')} alt='' width={18} height={18} aria-hidden />
    Watch
  </button>
);

const TestimonialVideoModal: FC<{
  payload: VideoModalPayload | null;
  onClose: () => void;
}> = ({ payload, onClose }) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isAnimated, setIsAnimated] = useState(false);

  useEffect(() => {
    if (!payload) {
      setIsAnimated(false);
      return;
    }
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus({ preventScroll: true });
    const raf = requestAnimationFrame(() => setIsAnimated(true));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
      setIsAnimated(false);
    };
  }, [payload, onClose]);

  useEffect(() => {
    if (!payload) return;
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    void video.play().catch(() => {
      /* autoplay blocked — controls still available */
    });
  }, [payload]);

  if (!payload) return null;

  return (
    <div
      ref={overlayRef}
      className='fixed inset-0 z-[100000] overflow-y-auto overscroll-contain'
      role='presentation'
    >
      <div className='pointer-events-none fixed inset-0' aria-hidden>
        <div className='absolute inset-0 bg-slate-900/35' />
        <div className='absolute inset-0 backdrop-blur-[18px] backdrop-saturate-[1.35]' />
        <div className='absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-slate-900/10' />
      </div>
      <div
        className='relative flex min-h-full items-center justify-center px-4 py-12 sm:py-16'
        onClick={onClose}
      >
        <div
          ref={dialogRef}
          role='dialog'
          aria-modal
          aria-label={`${payload.name} testimonial video`}
          tabIndex={-1}
          className='relative w-full max-w-[560px] rounded-2xl bg-white shadow-[0_32px_64px_-16px_rgba(15,23,42,0.45)] outline-none overflow-hidden'
          style={{
            transition: 'transform 400ms cubic-bezier(0.16, 1, 0.3, 1), opacity 280ms ease',
            transform: isAnimated ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.98)',
            opacity: isAnimated ? 1 : 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type='button'
            onClick={onClose}
            aria-label='Close video'
            className='absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-slate-700 hover:bg-white transition-colors shadow-md'
          >
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' aria-hidden>
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
          <video
            ref={videoRef}
            src={payload.videoUrl}
            controls
            controlsList='nodownload'
            playsInline
            preload='metadata'
            className='block w-full aspect-square bg-black object-contain'
            aria-label={`${payload.name} testimonial`}
          />
          <div className='px-5 py-4 border-t border-slate-100'>
            <p className='text-[15px] font-semibold text-slate-900 leading-tight'>{payload.name}</p>
            {(payload.role || payload.company) && (
              <p className='text-[13px] text-slate-500 mt-0.5'>
                {[payload.role, payload.company].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const MINI_CARD_HEIGHT = 240;
const MINI_AUTHOR_ROW_HEIGHT = 64;
const MINI_PADDING_BOTTOM = 18;

const MiniCard: FC<{ t: IMiniTestimonial; onWatch: (payload: VideoModalPayload) => void }> = ({ t, onWatch }) => (
  <div
    className='relative'
    style={{
      height: MINI_CARD_HEIGHT,
      padding: `24px 24px ${MINI_AUTHOR_ROW_HEIGHT + MINI_PADDING_BOTTOM + 18}px`,
      background: '#fff',
      border: `1px solid ${BORDER}`,
      borderRadius: 16,
    }}
  >
    {t.videoUrl && (
      // The -m/p pair grows the 26px icon's hit area to 44px without moving it.
      <button
        type='button'
        onClick={() => onWatch({ videoUrl: t.videoUrl as string, name: t.name, role: t.role, company: t.company })}
        title={`Watch ${t.name}'s testimonial`}
        aria-label={`Watch ${t.name}'s testimonial`}
        className='absolute top-4 right-4 -m-[9px] p-[9px] inline-flex items-center justify-center'
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={getImageUrl('icons/play.svg')} alt='' width={26} height={26} aria-hidden />
      </button>
    )}
    <p
      className='m-0'
      style={{
        fontFamily: FONT_BODY,
        fontSize: 15,
        lineHeight: 1.5,
        color: SLATE_800,
        textWrap: 'pretty',
        paddingRight: t.videoUrl ? 28 : 0,
      }}
    >
      &ldquo;{t.quote}&rdquo;
    </p>
    <div
      className='absolute flex items-center gap-2.5'
      style={{
        left: 24,
        right: 24,
        bottom: MINI_PADDING_BOTTOM,
        height: MINI_AUTHOR_ROW_HEIGHT,
      }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={t.img}
        alt={t.name}
        width={44}
        height={44}
        loading='lazy'
        style={{ borderRadius: '50%', objectFit: 'cover', background: SLATE_100, flexShrink: 0 }}
      />
      <div style={{ fontFamily: FONT_BODY, minWidth: 0, lineHeight: 1.35 }}>
        <a
          href={t.linkedinUrl}
          target='_blank'
          rel='noopener noreferrer'
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: SLATE_900,
            textDecoration: 'none',
          }}
        >
          {t.name}
        </a>
        <div style={{ fontSize: 12, color: SLATE_500 }}>{t.role}</div>
        <div style={{ fontSize: 12, color: SLATE_500 }}>{t.company}</div>
      </div>
    </div>
  </div>
);

const HeroCard: FC<{ t: IHeroTestimonial; onWatch: (payload: VideoModalPayload) => void }> = ({ t, onWatch }) => (
  <div
    className='grid overflow-hidden items-stretch grid-cols-1 md:grid-cols-[300px_1fr] xl:grid-cols-[380px_1fr_340px]'
    style={{
      border: `1px solid ${BORDER}`,
      borderRadius: 20,
      minHeight: 420,
    }}
  >
    <div
      className='md:row-span-2 xl:row-span-1 h-full'
      style={{ background: SLATE_100, minHeight: 280 }}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={t.img}
        alt={t.name}
        className='block h-full w-full object-cover'
        style={{ minHeight: 280 }}
      />
    </div>
    <div className='h-full' style={{ padding: '48px 48px 44px', background: '#fff' }}>
      <div style={{ marginBottom: 20 }}>
        <QuoteGlyph />
      </div>
      <blockquote
        className='m-0'
        style={{
          fontFamily: FONT_DISPLAY,
          fontWeight: 500,
          fontSize: 30,
          lineHeight: 1.22,
          letterSpacing: '-0.015em',
          color: '#000',
          textWrap: 'balance',
        }}
      >
        {t.quote}
      </blockquote>
      <div
        className='flex items-center justify-between gap-4 flex-wrap'
        style={{ marginTop: 32, fontFamily: FONT_BODY }}
      >
        <div style={{ lineHeight: 1.4 }}>
          <a
            href={t.linkedinUrl}
            target='_blank'
            rel='noopener noreferrer'
            style={{
              display: 'block',
              fontSize: 15,
              fontWeight: 600,
              color: SLATE_900,
              textDecoration: 'none',
            }}
          >
            {t.name}
          </a>
          <div style={{ fontSize: 13, color: SLATE_500 }}>{t.role}</div>
          <div style={{ fontSize: 13, color: SLATE_500 }}>{t.company}</div>
        </div>
        {t.videoUrl && (
          <WatchBadge onClick={() => onWatch({ videoUrl: t.videoUrl as string, name: t.name, role: t.role, company: t.company })} />
        )}
      </div>
    </div>
    <div
      className='relative flex flex-col justify-between overflow-hidden md:col-span-2 xl:col-span-1 h-full'
      style={{
        background: BLUE_800,
        color: '#fff',
        padding: '48px 40px',
        minHeight: 260,
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          width: 260,
          height: 260,
          borderRadius: '50%',
          background: MINT_400,
          filter: 'blur(120px)',
          opacity: 0.45,
          top: -60,
          right: -60,
        }}
      />
      <div
        className='relative'
        style={{
          fontFamily: FONT_BODY,
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: MINT_300,
        }}
      >
        Result
      </div>
      <div className='relative'>
        <div
          style={{
            fontFamily: FONT_DISPLAY,
            fontWeight: 500,
            fontSize: 'clamp(2.75rem, 8vw, 6rem)',
            lineHeight: 0.9,
            letterSpacing: '-0.04em',
            color: '#fff',
          }}
        >
          {t.metric.value}
        </div>
        <div
          style={{
            fontFamily: FONT_BODY,
            fontSize: 15,
            color: 'rgba(255,255,255,0.75)',
            marginTop: 12,
          }}
        >
          {t.metric.label}
        </div>
      </div>
    </div>
  </div>
);

const GrowSection: FC = () => {
  const [videoPayload, setVideoPayload] = useState<VideoModalPayload | null>(null);
  const openVideo = useCallback((payload: VideoModalPayload) => setVideoPayload(payload), []);
  const closeVideo = useCallback(() => setVideoPayload(null), []);
  return (
    <section className='section-grow bg-white px-4 md:px-7.5 xl:px-25 pt-14 md:pt-20 pb-16 md:pb-20'>
      <FadeIn variant='down' delay={0.2}>
        <div
          className='mx-auto text-center'
          style={{ maxWidth: 1240, marginBottom: 40 }}
        >
          <h2
            className='m-0'
            style={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 500,
              fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: '#000',
            }}
          >
            Built on the work of real <span style={{ color: BLUE_500 }}>revenue</span> teams.
          </h2>
        </div>
      </FadeIn>

      <FadeIn variant='up' delay={0.25}>
        <div className='mx-auto' style={{ maxWidth: 1240 }}>
          <HeroCard t={HERO} onWatch={openVideo} />
        </div>
      </FadeIn>

      <FadeIn variant='up' delay={0.3}>
        <div className='mx-auto' style={{ maxWidth: 1240, marginTop: 16 }}>
          <div className='flex md:grid md:grid-cols-3 xl:grid-cols-5 gap-3.5 overflow-x-auto md:overflow-x-visible snap-x snap-mandatory md:snap-none -mx-4 md:mx-0 px-4 md:px-0 pb-2 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
            {MINIS.map((t, i) => (
              <div key={i} className='snap-center shrink-0 w-[80%] sm:w-[55%] md:w-auto md:shrink'>
                <MiniCard t={t} onWatch={openVideo} />
              </div>
            ))}
          </div>
        </div>
      </FadeIn>

      <TestimonialVideoModal payload={videoPayload} onClose={closeVideo} />
    </section>
  );
};

export default GrowSection;
